import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction
} from '@reduxjs/toolkit'
import {omit, mapObjIndexed} from 'ramda'
import {Auth0DecodedHash} from 'auth0-js'

import {
  sendMessage,
  makeOtpLogin,
  startLoginSignUp,
  answerCustomChallenge,
  makeSocialLogin,
  parseUserTokenFromCallbackUrl,
  SendMessageSuccess,
  SendMessageError,
  GenericAuth0Error,
  updateUserAttributes,
  uploadUserPicture,
  updateProfilePictureUrl,
  upgradeUserToPlus
} from './thunks'

import {
  UserPhoneNumberType,
  MessageChannelsType,
  MessageAttemptsHistoryType,
  ChannelsType
} from '../types/Passwordless'

import {
  AudienceToken,
  AuthenticatedUser,
  AuthenticationFlows,
  ClaimsType,
  ShowPasswordlessLoginPayload,
  UserAction
} from '../types/Auth'

import {
  CognitoSession,
  User,
  CognitoError,
  UserAttributes
} from '../types/Cognito'

import {SMS_KEY, WHATSAPP_KEY} from '../constants'
import {persistCognitoUserTierOnCookie} from '../business/persistData'
import {isValidURL} from '../../utils/url'
import {EnabledBrands} from '../../brand/types'

/**
 * The Auth Redux state Slice
 */
export type StateType = {
  /** Authentication errors */
  error: {
    errorType?: string
    errorMessage?: string
  }
  lastAction?: string
  /** Handles the UI state to manage the passwordless login modal visibility */
  isPasswordlessLoginVisible: boolean
  /** ??? */
  status: string
  /** The user's phone number */
  userPhoneNumber: UserPhoneNumberType | null | undefined
  /** The type of channel the user used for authentication */
  selectedChannel: MessageChannelsType | null | undefined
  /** The available channels for authentication */
  channels: ChannelsType
  /** Tracks the history of login attempts from the user */
  attemptsHistory: MessageAttemptsHistoryType
  /** Handles the UI state to manage the "resending code" message visibility */
  isUserResending: boolean
  /** The authenticated user id */
  userId: string | null | undefined
  /** Which flow the user is using to login/signup */
  flow: AuthenticationFlows
  /** Which component the user triggered login/signup */
  sourceComponent: string
  /** Is the user privileged */
  isPrivileged: boolean
  /** Is the user authenticated */
  isAuthenticated: boolean
  /** Has audience token */
  isAudience: boolean
  /** The user */
  user: User
  /** The loading state when authenticating the user */
  isLoading: boolean
  /** The URL path name */
  loginPathName: string
  /** Cognito response */
  cognitoSession: CognitoSession | null
  /** Profile picture URL from Amplify Storage.get */
  profilePictureUrl: string | null
  /** The brands that enabled to perform authentication */
  enabledBrands: EnabledBrands[]
}

/** @private */
export const INIT_STATUS = 'init'

export const initialChannelsState: ChannelsType = {
  [SMS_KEY]: {
    name: 'SMS',
    enabled: true,
    attempts: 0
  },
  [WHATSAPP_KEY]: {
    name: 'WhatsApp',
    enabled: true,
    attempts: 0
  }
}

export const initialState = {
  isPasswordlessLoginVisible: false,
  isUserResending: false,
  error: {},
  status: INIT_STATUS,
  userPhoneNumber: null,
  channels: initialChannelsState,
  selectedChannel: null,
  userId: null,
  flow: null,
  isPrivileged: false,
  isAuthenticated: false,
  isAudience: false,
  user: null,
  isLoading: true,
  loginPathName: null,
  cognitoSession: null,
  enabledBrands: []
}

const {actions, reducer} = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    showPasswordlessLogin: (
      state: StateType,
      action: PayloadAction<ShowPasswordlessLoginPayload>
    ) => {
      state.isPasswordlessLoginVisible = true
      state.flow = action.payload.flow
      state.sourceComponent = action.payload.sourceComponent
      state.status = action.type
    },
    hidePasswordlessLogin: (state: StateType) => {
      state.isPasswordlessLoginVisible = false
      state.status = INIT_STATUS
      state.cognitoSession = null
    },
    cleanUpLoginErrors: (state: StateType) => {
      state.error = {}
    },
    cleanUpLoginStatus: (state: StateType) => {
      return {
        ...state,
        ...omit(
          [
            'channels',
            'isPasswordlessLoginVisible',
            'isLoading',
            'enabledBrands'
          ],
          initialState
        )
      }
    },
    setResendMessage: (state: StateType, action: PayloadAction<string>) => {
      state.status = action.type
      state.isUserResending = true
    },
    setMessageAttemptsHistory: (
      state: StateType,
      action: PayloadAction<MessageAttemptsHistoryType>
    ) => {
      state.channels = mapObjIndexed(
        (channel, channelKey) => ({
          ...channel,
          attempts: action.payload[channelKey]
        }),
        state.channels
      )
    },
    identifyAuthenticatedUser: (
      state: StateType,
      action: PayloadAction<ClaimsType>
    ) => {
      state.userId = action.payload?.sub
    },
    setIsPrivileged: (state: StateType, action: PayloadAction<boolean>) => {
      state.isPrivileged = action.payload
    },
    setIsAuthenticated: (
      state: StateType,
      action: PayloadAction<AuthenticatedUser>
    ) => {
      state.isAuthenticated = action.payload.isAuthenticated
    },
    setIsAudience: (state: StateType, action: PayloadAction<AudienceToken>) => {
      state.isAudience = action.payload.isAudience
    },
    setUser: (state: StateType, action: PayloadAction<UserAction>) => {
      state.user = action.payload.user
      state.isAuthenticated = action.payload.isAuthenticated
      state.isPrivileged = action.payload.isPrivileged
      persistCognitoUserTierOnCookie(action.payload.user)
    },
    setIsLoading: (state: StateType, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setLoginPathName: (state: StateType, action: PayloadAction<string>) => {
      state.loginPathName = action.payload
    },
    setEnabledBrands: (
      state: StateType,
      action: PayloadAction<EnabledBrands[]>
    ) => {
      state.enabledBrands = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(
        sendMessage.fulfilled.type,
        (state: StateType, action: PayloadAction<SendMessageSuccess>) => {
          const {type, payload} = action
          const {userPhoneNumber, selectedChannel} = payload

          state.status = type
          state.selectedChannel = selectedChannel
          state.userPhoneNumber = userPhoneNumber
          state.channels[selectedChannel].attempts += 1
        }
      )
      .addCase(
        sendMessage.rejected.type,
        (state: StateType, action: PayloadAction<SendMessageError>) => {
          state.status = action.type
          state.error = {
            errorType: action.payload.error,
            errorMessage: action.payload.error_description
          }
        }
      )
      .addCase(
        makeOtpLogin.rejected.type,
        (state: StateType, action: PayloadAction<GenericAuth0Error>) => {
          state.error = {
            errorType: 'PasswordlessLogin',
            errorMessage: action.payload
          }

          state.status = action.type
        }
      )
      .addCase(
        makeSocialLogin.rejected.type,
        (state: StateType, action: PayloadAction<GenericAuth0Error>) => {
          state.error = {
            errorType: 'SocialLogin',
            errorMessage: action.payload
          }

          state.status = action.type
        }
      )
      .addCase(
        parseUserTokenFromCallbackUrl.fulfilled.type,
        (state: StateType, action: PayloadAction<Auth0DecodedHash>) => {
          state.status = action.type
        }
      )
      .addCase(
        parseUserTokenFromCallbackUrl.rejected.type,
        (state: StateType, action: PayloadAction<GenericAuth0Error>) => {
          state.status = action.type

          state.error = {
            errorType: 'ParseUserTokenError',
            errorMessage: action.payload
          }
        }
      )
      .addCase(
        startLoginSignUp.fulfilled.type,
        (state: StateType, action: PayloadAction<SendMessageSuccess>) => {
          const {type, payload} = action

          const {userPhoneNumber, selectedChannel, cognitoSession} = payload

          state.status = type
          state.selectedChannel = selectedChannel
          state.userPhoneNumber = userPhoneNumber
          state.channels[selectedChannel].attempts += 1
          state.cognitoSession = cognitoSession
        }
      )
      .addCase(
        startLoginSignUp.rejected.type,
        (state: StateType, action: PayloadAction<CognitoError>) => {
          state.status = action.type
          state.error = {
            errorType: action.payload?.code,
            errorMessage: action.payload?.name
          }
        }
      )
      .addCase(
        answerCustomChallenge.fulfilled.type,
        (state: StateType, action: PayloadAction<User>) => {
          state.user = action.payload
          state.cognitoSession = null
          state.isAuthenticated = true
          state.isPrivileged = true
          state.isLoading = false
          state.isPasswordlessLoginVisible = false
          persistCognitoUserTierOnCookie(action.payload)
        }
      )
      .addCase(
        answerCustomChallenge.rejected.type,
        (state: StateType, action: PayloadAction<any>) => {
          state.error = {
            errorType: 'PasswordlessLogin',
            errorMessage: action.payload
          }
          state.status = action.type
        }
      )
      .addCase(
        updateProfilePictureUrl.fulfilled.type,
        (state: StateType, action: PayloadAction<any>) => {
          state.profilePictureUrl = isValidURL(state.user.attributes.picture)
            ? state.user.attributes.picture
            : action.payload
        }
      )
      .addCase(
        upgradeUserToPlus.fulfilled.type,
        (state: StateType, action: PayloadAction<any>) => {
          state.user.attributes['custom:app_data'] = action.payload
          persistCognitoUserTierOnCookie(state.user)
        }
      )
      .addMatcher(
        isFulfilled(updateUserAttributes, uploadUserPicture),
        (state: StateType, action: PayloadAction<UserAttributes>) => {
          state.lastAction = action.type
          state.user = {
            ...state.user,
            attributes: action.payload
          }
          state.error = null
        }
      )
      .addMatcher(
        isPending(updateUserAttributes, uploadUserPicture),
        (state: StateType, action: PayloadAction<any>) => {
          state.lastAction = action.type
          state.error = null
        }
      )
      .addMatcher(
        isRejected(updateUserAttributes, uploadUserPicture),
        (state: StateType, action: PayloadAction<any>) => {
          state.lastAction = action.type
          state.error = {
            errorType: 'profileUpdate',
            errorMessage: action?.payload?.message
          }
        }
      )
  }
})

export const {
  showPasswordlessLogin,
  hidePasswordlessLogin,
  cleanUpLoginErrors,
  cleanUpLoginStatus,
  setResendMessage,
  setMessageAttemptsHistory,
  identifyAuthenticatedUser,
  setIsPrivileged,
  setIsAuthenticated,
  setIsAudience,
  setUser,
  setIsLoading,
  setLoginPathName,
  setEnabledBrands
} = actions

export default reducer
